import { ReactNode } from 'react';
import { en, fi } from 'make-plural';
import { i18n } from '@lingui/core';
import { I18nProvider as LinguiProvider, useLingui } from '@lingui/react';

import { useOptions } from '~contexts/OptionsContext';
import { loadPersisted, readLegacyZustand } from '~utils/persistedState';

export type Locale = 'fi' | 'en';

export const SUPPORTED_LOCALES: Locale[] = ['fi', 'en'];
const DEFAULT_LOCALE: Locale = 'en';

async function loadMessages(locale: Locale) {
  // @vite-ignore
  const { messages } = await import(`../locales/${locale}/messages.ts`); // Vite cannot analyze dynamic imports
  return messages;
}

export async function initMessages() {
  // Read the persisted locale directly from localStorage (runs before React).
  const persisted = loadPersisted(
    'options',
    readLegacyZustand('option-store', { locale: DEFAULT_LOCALE })
  );
  const locale: Locale = SUPPORTED_LOCALES.includes(persisted.locale as Locale)
    ? (persisted.locale as Locale)
    : DEFAULT_LOCALE;

  const messages = await loadMessages(locale);

  i18n.loadLocaleData({ fi: { plurals: fi }, en: { plurals: en } });
  i18n.load(locale, messages);
  i18n.activate(locale);
}

export function useI18n() {
  const lingui = useLingui();
  const { setLocale } = useOptions();

  async function changeLocale(locale: Locale) {
    try {
      const newMessages = await loadMessages(locale);
      lingui.i18n.load(locale, newMessages);
      lingui.i18n.activate(locale);
      setLocale(locale);
    } catch (error) {
      console.log(`> Failed to load messages for locale: ${locale}`, error);
    }
  }

  return { i18n: lingui.i18n, changeLocale };
}

export function I18nProvider({ children }: { children: ReactNode }) {
  return <LinguiProvider i18n={i18n}>{children}</LinguiProvider>;
}
